// Sprint ONBOARD ONB.C3 (A.5, #831) — Drive OAuth sub-wizard.
//
// Mounted at /setup/authorize-gdrive (replacing A.3's SetupSubWizardPlaceholder)
// and reused in Settings via SettingsDriveAuthPage (mode='settings').
// A.3 did NOT ship a shared multi-step stepper for the /setup sub-wizards —
// SetupWizardPage uses an inline progress strip and each sub-route "owns its
// 4-step flow end-to-end" — so this page renders its OWN step UI in the same
// inline-styles + CSS-var idiom as SetupWizardPage (no WizardStepper import).
//
// Flow: intro → launch (popup to accounts.google.com) → the popup bounces back
// through /setup/oauth-bounce which postMessages the result here → verifying
// (poll GET /tenant-integrations/me until authorized) → success → auto-return.
// Effects use promise-chain (.then/.catch) callbacks, never async-in-effect, to
// stay clear of react-hooks/set-state-in-effect.
//
// DRV-5a (#2883):
// - mode='settings' (admin Settings path only) opens on DriveConnectionPanel
//   ('home'): status, Reconnect, Disconnect. A `needs_confirmation` result
//   shows DriveAccountSwitchDialog. mode='setup' (the wizard; an assigned TL
//   can use it) never renders either: no admin controls, no metadata.
// - #1219 recovery, on all three paths the recon found (lost postMessage,
//   a replayed callback bouncing 'error', the verify timeout):
//   · mode='setup' reads /me on load ('checking'); authorized → 'connected'.
//   · entering 'error' re-reads /me; a connection made by this attempt →
//     'success' instead of the error.
//   · while the popup is open in 'launch', /me is polled with backoff and a
//     ceiling, so a lost window.opener cannot leave the page stuck.
//   "Made by this attempt" compares /me with a baseline read when the popup
//   opens (status, last_verified_at, account) — so a Reconnect of an already
//   authorized tenant is not reported done before Google has answered.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { fetchDriveIntegration, type DriveIntegration } from '../lib/driveIntegration';
import DriveConnectionPanel from '../components/settings/DriveConnectionPanel';
import DriveAccountSwitchDialog, { CONFIRM_ACCOUNT_CHANGE_PATH } from '../components/settings/DriveAccountSwitchDialog';

export type DriveAuthStep =
  | 'checking' | 'connected' | 'home'
  | 'intro' | 'launch' | 'verifying'
  | 'success' | 'cancelled' | 'admin_policy_enforced' | 'needs_confirmation' | 'error';

const POLL_INTERVAL_MS = 500;
const POLL_CEILING_MS = 10_000;
const SUCCESS_REDIRECT_MS = 2_000;
const OAUTH_RESULT_KIND = 'gdrive-oauth-result';
// Launch-step poll (#1219 path c): the user may sit on Google's consent screen
// for a while, so this is slower and longer than the verify poll.
export const LAUNCH_POLL_INITIAL_MS = 1_000;
export const LAUNCH_POLL_MAX_MS = 5_000;
export const LAUNCH_POLL_CEILING_MS = 5 * 60_000;

/** True when `now` is a connection this attempt made, judged against `baseline`. */
function isNewConnection(now: DriveIntegration, baseline: DriveIntegration): boolean {
  if (now.status !== 'authorized') return false;
  if (baseline.status !== 'authorized') return true;
  return (now.metadata?.last_verified_at ?? null) !== (baseline.metadata?.last_verified_at ?? null)
      || (now.metadata?.account?.permissionId ?? null) !== (baseline.metadata?.account?.permissionId ?? null);
}

function logPollError(where: string, err: unknown) {
  console.debug(`[AuthorizeGDrivePage] ${where}: /tenant-integrations/me read failed`, err);
}

interface AuthorizeGDrivePageProps {
  /** 'setup' (default) returns to /setup; 'settings' shows the connection panel. */
  mode?: 'setup' | 'settings';
}

export default function AuthorizeGDrivePage({ mode = 'setup' }: AuthorizeGDrivePageProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<DriveAuthStep>(mode === 'settings' ? 'home' : 'checking');
  const [launching, setLaunching] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  // In the error step, after its /me re-read: Drive is still connected, either
  // with the old connection unchanged, or with no baseline to tell.
  const [connectedNote, setConnectedNote] = useState<null | 'unchanged' | 'unknown'>(null);
  const popupRef = useRef<Window | null>(null);
  // /me as it was when the popup opened (or on load); null = unknown.
  const baselineRef = useRef<DriveIntegration | null>(null);
  // R1 P2-2 — inside a confirmed account switch, a retry (pop-up blocked)
  // must ask for a fresh account-bound URL, not the unbound /authorize one.
  const confirmFlowRef = useRef(false);

  const homeStep: DriveAuthStep = mode === 'settings' ? 'home' : 'intro';
  // '/settings' is the existing Settings landing; '/settings/integrations' is
  // NOT a registered route (the only integrations route is .../gdrive, which is
  // this very page — returning there would loop).
  const returnPath = mode === 'settings' ? '/settings' : '/setup';

  function goTo(next: DriveAuthStep) {
    if (next === 'home' || next === 'intro') confirmFlowRef.current = false;
    setConnectedNote(null);
    setStep(next);
  }

  // ── Status on load (wizard mode; the Settings panel reads its own) ─────────
  useEffect(() => {
    if (mode !== 'setup') return;
    let active = true;
    fetchDriveIntegration()
      .then((now) => {
        if (!active) return;
        baselineRef.current = now;
        // Only from 'checking': a pop-up result that already arrived wins.
        setStep((s) => (s === 'checking' ? (now.status === 'authorized' ? 'connected' : 'intro') : s));
      })
      .catch((err: unknown) => {
        if (!active) return;
        logPollError('load', err);
        setStep((s) => (s === 'checking' ? 'intro' : s));
      });
    return () => { active = false; };
  }, [mode]);

  // ── Pop-up result relay (from /setup/oauth-bounce via postMessage) ──────────
  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;            // origin defense
      const data = e.data as { kind?: string; status?: string };
      if (data?.kind !== OAUTH_RESULT_KIND || !data.status) return; // kind defense
      popupRef.current = null;
      setPopupOpen(false);
      setConnectedNote(null);
      if (data.status === 'success') setStep('verifying');
      else if (data.status === 'cancelled') setStep('cancelled');
      else if (data.status === 'admin_policy_enforced') setStep('admin_policy_enforced');
      else if (data.status === 'needs_confirmation') setStep('needs_confirmation');
      else setStep('error');
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // ── Polling fallback while verifying (belt-and-suspenders for a missed msg) ──
  useEffect(() => {
    if (step !== 'verifying') return;
    let active = true;
    const poll = setInterval(() => {
      apiFetch<{ status: string | null }>('/tenant-integrations/me?provider=gdrive')
        .then((res) => {
          if (!active) return;
          if (res.status === 'authorized') { clearInterval(poll); setStep('success'); }
          else if (res.status === 'admin_policy_enforced') { clearInterval(poll); setStep('admin_policy_enforced'); }
        })
        .catch((err: unknown) => logPollError('verify poll', err)); // the ceiling handles the timeout
    }, POLL_INTERVAL_MS);
    const ceiling = setTimeout(() => {
      if (!active) return;
      // Stop the interval and flip `active` synchronously so an in-flight poll
      // that resolves after the ceiling fires can't overwrite 'error' with
      // 'success' before React runs this effect's cleanup.
      active = false;
      clearInterval(poll);
      setStep('error');
    }, POLL_CEILING_MS);
    return () => { active = false; clearInterval(poll); clearTimeout(ceiling); };
  }, [step]);

  // ── #1219 (c): poll while the pop-up is open, in case no message arrives ───
  useEffect(() => {
    if (step !== 'launch' || !popupOpen) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let delay = LAUNCH_POLL_INITIAL_MS;
    const startedAt = Date.now();
    const tick = () => {
      fetchDriveIntegration()
        .then((now) => {
          if (!active) return;
          const baseline = baselineRef.current;
          // An unknown baseline can't tell a new connection from an old one;
          // leave it to the message or the error-step re-read.
          if (baseline && isNewConnection(now, baseline)) {
            active = false;
            popupRef.current = null;
            setPopupOpen(false);
            setStep('success');
          }
        })
        .catch((err: unknown) => logPollError('launch poll', err))
        .finally(() => {
          if (!active) return;
          if (Date.now() - startedAt >= LAUNCH_POLL_CEILING_MS) {
            active = false;
            popupRef.current = null;
            setPopupOpen(false);
            setStep('error');
            return;
          }
          delay = Math.min(Math.round(delay * 1.5), LAUNCH_POLL_MAX_MS);
          timer = setTimeout(tick, delay);
        });
    };
    timer = setTimeout(tick, delay);
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [step, popupOpen]);

  // ── #1219 (a)/(b): the error step re-reads /me before standing ─────────────
  useEffect(() => {
    if (step !== 'error') return;
    let active = true;
    fetchDriveIntegration()
      .then((now) => {
        if (!active || now.status !== 'authorized') return;
        const baseline = baselineRef.current;
        // R2 P1 — with no baseline, an authorized row may be the OLD connection
        // left untouched by a failed reconnect (F-1): don't claim success.
        if (!baseline) setConnectedNote('unknown');
        else if (isNewConnection(now, baseline)) setStep('success');
        else setConnectedNote('unchanged'); // this attempt failed; the old connection stands
      })
      .catch((err: unknown) => logPollError('error re-read', err));
    return () => { active = false; };
  }, [step]);

  // ── Success auto-return ─────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 'success') return;
    // A completed attempt ends any account switch: the next Reconnect is a
    // plain one (the server cleared pendingAccount; a confirm POST would 409).
    confirmFlowRef.current = false;
    const t = setTimeout(() => {
      if (mode === 'settings') setStep('home');
      else navigate(returnPath);
    }, SUCCESS_REDIRECT_MS);
    return () => clearTimeout(t);
  }, [step, navigate, returnPath, mode]);

  /** Opens the consent pop-up for an auth URL, after a fresh baseline read. */
  function openPopup(getAuthUrl: () => Promise<{ authUrl: string }>) {
    setLaunching(true);
    setPopupBlocked(false);
    Promise.all([
      fetchDriveIntegration().catch((err: unknown) => { logPollError('baseline', err); return null; }),
      getAuthUrl(),
    ])
      .then(([baseline, res]) => {
        baselineRef.current = baseline;
        const popup = window.open(res.authUrl, 'gdrive-oauth', 'width=500,height=650');
        if (!popup) { setPopupBlocked(true); return; }
        popupRef.current = popup;
        setPopupOpen(true);
      })
      .catch(() => goTo('error'))
      .finally(() => setLaunching(false));
  }

  function launch() {
    openPopup(confirmFlowRef.current
      ? () => apiFetch<{ authUrl: string }>(CONFIRM_ACCOUNT_CHANGE_PATH, { method: 'POST' })
      : () => apiFetch<{ authUrl: string }>('/auth/google/drive/authorize'));
  }

  function confirmAccountSwitch(authUrl: string) {
    goTo('launch');
    confirmFlowRef.current = true;
    openPopup(() => Promise.resolve({ authUrl }));
  }

  function cancelLaunch() {
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    popupRef.current = null;
    setPopupOpen(false);
    setPopupBlocked(false);
    goTo(homeStep);
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  const outerStyle = mode === 'settings' ? settingsContainerStyle : pageStyle;

  if (step === 'home') {
    return (
      <div data-testid="authorize-gdrive" style={outerStyle}>
        <DriveConnectionPanel onConnect={() => goTo('launch')} />
      </div>
    );
  }

  return (
    <div data-testid="authorize-gdrive" style={outerStyle}>
      <div style={cardStyle}>
        <p style={eyebrowStyle}>Storage integration</p>
        <h1 style={titleStyle}>Authorise Google Drive</h1>

        {step === 'checking' && (
          <div data-testid="gdrive-checking" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={spinnerStyle} aria-hidden />
            <p style={{ ...subtitleStyle, margin: 0 }}>Checking the Drive connection…</p>
          </div>
        )}

        {step === 'connected' && (
          <>
            <p data-testid="gdrive-already-connected" style={subtitleStyle}>
              Google Drive is already connected for your workspace. There&rsquo;s nothing more to do here.
            </p>
            <button data-testid="gdrive-continue-connected" style={primaryBtnStyle} onClick={() => navigate(returnPath)}>
              Continue
            </button>
          </>
        )}

        {step === 'intro' && (
          <>
            <p style={subtitleStyle}>
              BluePrint uses Google Drive to import your documents and export deliverables.
              We only request the <strong>drive.file</strong> scope — access is limited to files
              you create or open with BluePrint.
            </p>
            <button data-testid="gdrive-continue" style={primaryBtnStyle} onClick={() => goTo('launch')}>
              Continue
            </button>
          </>
        )}

        {step === 'launch' && (
          <>
            <p style={subtitleStyle}>
              A Google window will open for you to grant access. Keep this tab open — it updates
              automatically when you finish.
            </p>
            {popupBlocked && (
              <p data-testid="gdrive-popup-blocked" style={warnStyle}>
                Your browser blocked the pop-up. Allow pop-ups for this site, then try again.
              </p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button data-testid="gdrive-authorize" style={primaryBtnStyle} onClick={launch} disabled={launching}>
                {launching ? 'Opening…' : 'Authorise Drive access'}
              </button>
              <button data-testid="gdrive-cancel" style={secondaryBtnStyle} onClick={cancelLaunch} disabled={launching}>
                Cancel
              </button>
            </div>
          </>
        )}

        {step === 'verifying' && (
          <div data-testid="gdrive-verifying" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={spinnerStyle} aria-hidden />
            <p style={{ ...subtitleStyle, margin: 0 }}>Confirming access…</p>
          </div>
        )}

        {step === 'success' && (
          <div data-testid="gdrive-success" style={{ textAlign: 'center' }}>
            <div style={checkCircleStyle} aria-hidden>✓</div>
            <h2 style={{ ...titleStyle, fontSize: 18 }}>Drive connected</h2>
            <p style={subtitleStyle}>Taking you back…</p>
            <button
              style={secondaryBtnStyle}
              onClick={() => (mode === 'settings' ? goTo('home') : navigate(returnPath))}
            >
              Return now
            </button>
          </div>
        )}

        {step === 'cancelled' && (
          <>
            <p data-testid="gdrive-cancelled" style={subtitleStyle}>
              Authorisation was cancelled. You can try again whenever you&rsquo;re ready.
            </p>
            <button data-testid="gdrive-try-again" style={primaryBtnStyle} onClick={() => goTo(homeStep)}>
              Try again
            </button>
          </>
        )}

        {step === 'admin_policy_enforced' && (
          <>
            <p data-testid="gdrive-admin-policy" style={subtitleStyle}>
              Your Google Workspace administrator has restricted access to third-party apps, so we
              couldn&rsquo;t connect Drive. Ask your admin to allow BluePrint, then try again.
            </p>
            <button data-testid="gdrive-try-again" style={primaryBtnStyle} onClick={() => goTo(homeStep)}>
              Try again
            </button>
          </>
        )}

        {/* DRV-1 (#2879, D9): the existing connection is untouched. Settings
            (admin) confirms the switch here; the wizard keeps the message,
            since a TL can't confirm (server: admin rank only). */}
        {step === 'needs_confirmation' && mode === 'settings' && (
          <DriveAccountSwitchDialog onConfirmed={confirmAccountSwitch} onCancel={() => goTo('home')} />
        )}
        {step === 'needs_confirmation' && mode !== 'settings' && (
          <>
            <p data-testid="gdrive-needs-confirmation" style={subtitleStyle}>
              You signed in with a different Google account from the one Drive is already connected
              to, so nothing was changed. Drive stays connected to the original account. Switching
              accounts needs confirmation from a workspace admin.
            </p>
            <button style={secondaryBtnStyle} onClick={() => navigate(returnPath)}>Return</button>
          </>
        )}

        {step === 'error' && (
          <>
            <p data-testid="gdrive-error" style={subtitleStyle}>
              Something went wrong connecting Drive. Please try again.
            </p>
            {connectedNote === 'unchanged' && (
              <p data-testid="gdrive-error-still-connected" style={subtitleStyle}>
                Drive is still connected with its existing account; nothing was changed.
              </p>
            )}
            {connectedNote === 'unknown' && (
              <p data-testid="gdrive-error-connected-unknown" style={subtitleStyle}>
                Drive is connected for your workspace, but we couldn&rsquo;t tell whether this attempt
                changed anything. Check the account shown in Settings → Google Drive.
              </p>
            )}
            <button data-testid="gdrive-try-again" style={primaryBtnStyle} onClick={() => goTo(homeStep)}>
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Styles (mirrors SetupWizardPage's inline-style + CSS-var idiom) ─────────────

const pageStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '48px 24px', background: 'var(--bg)',
};
const settingsContainerStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', padding: '24px',
};
const cardStyle: React.CSSProperties = {
  width: '100%', maxWidth: 480, background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-xl, 16px)', boxShadow: 'var(--shadow-lg)', padding: 40,
};
const eyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0,
};
const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '4px 0 0',
};
const subtitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '12px 0 24px',
};
const warnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error, #ef4444)', margin: '0 0 12px',
};
const primaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, padding: '10px 24px',
  background: 'var(--primary, #7c3aed)', color: '#fff', border: 'none',
  borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500, padding: '10px 24px',
  background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
};
const spinnerStyle: React.CSSProperties = {
  width: 20, height: 20, borderRadius: '50%', border: '2px solid var(--border)',
  borderTopColor: 'var(--primary, #7c3aed)', display: 'inline-block', animation: 'spin 0.8s linear infinite',
};
const checkCircleStyle: React.CSSProperties = {
  width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', color: '#16a34a',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 16px',
};
